import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { type FormEvent, useState } from "react";

import { Layout } from "@/components/layout";

export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    checkIn: "",
    checkOut: "",
    guests: "2",
    villaPreference: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitMessage("Thank you for your inquiry! We will contact you shortly.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          checkIn: "",
          checkOut: "",
          guests: "2",
          villaPreference: "",
          message: "",
        });
      } else {
        setSubmitMessage(
          "There was an error sending your message. Please try again or contact us directly."
        );
      }
    } catch {
      setSubmitMessage(
        "There was an error sending your message. Please try again or contact us directly."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[350px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/11434418/pexels-photo-11434418.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Contact Us
            </h1>
            <p className="text-xl text-white/95 font-light tracking-wide">
              We're here to help plan your perfect escape
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-3xl font-bold text-stone-900 mb-6">Get in Touch</h2>
              <div className="w-24 h-px bg-luxury-gold mb-8" />

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-stone-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Location</h3>
                    <p className="text-stone-600 text-sm">
                      Beachfront Road
                      <br />
                      Koh Samui, Surat Thani
                      <br />
                      84140 Thailand
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-stone-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Phone</h3>
                    <p className="text-stone-600 text-sm">+66 77 XXX XXX</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-stone-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Email</h3>
                    <p className="text-stone-600 text-sm">info@theamor.com</p>
                    <p className="text-stone-600 text-sm">reservations@theamor.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-stone-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">Availability</h3>
                    <p className="text-stone-600 text-sm">24/7 Concierge Service</p>
                    <p className="text-stone-600 text-sm">Reservations: 8 AM - 10 PM (GMT+7)</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-stone-50 border border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-3">Airport Transfer</h3>
                <p className="text-sm text-stone-600 leading-relaxed mb-3">
                  Complimentary airport shuttle service available for all guests. Please provide
                  your flight details at least 24 hours in advance.
                </p>
                <p className="text-sm text-stone-600">
                  <strong>Distance from Samui Airport:</strong> 15 minutes
                </p>
              </div>
            </div>

            {/* Reservation Form */}
            <div className="lg:col-span-2">
              <div className="bg-stone-50 p-8 border border-stone-200">
                <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">
                  Reservation Inquiry
                </h2>
                <p className="text-stone-600 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                {submitMessage && (
                  <div
                    className={`mb-6 p-4 border ${submitMessage.includes("Thank you") ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}
                  >
                    {submitMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-stone-900 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="checkIn"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Check-in Date *
                      </label>
                      <input
                        type="date"
                        id="checkIn"
                        name="checkIn"
                        required
                        value={formData.checkIn}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="checkOut"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Check-out Date *
                      </label>
                      <input
                        type="date"
                        id="checkOut"
                        name="checkOut"
                        required
                        value={formData.checkOut}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="guests"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Number of Guests *
                      </label>
                      <select
                        id="guests"
                        name="guests"
                        required
                        value={formData.guests}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      >
                        <option value="1">1 Guest</option>
                        <option value="2">2 Guests</option>
                        <option value="3">3 Guests</option>
                        <option value="4">4 Guests</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="villaPreference"
                        className="block text-sm font-semibold text-stone-900 mb-2"
                      >
                        Villa Preference
                      </label>
                      <select
                        id="villaPreference"
                        name="villaPreference"
                        value={formData.villaPreference}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      >
                        <option value="">No preference</option>
                        <option value="1">Villa 1</option>
                        <option value="2">Villa 2</option>
                        <option value="3">Villa 3</option>
                        <option value="4">Villa 4</option>
                        <option value="5">Villa 5</option>
                        <option value="6">Villa 6</option>
                        <option value="7">Villa 7</option>
                        <option value="8">Villa 8</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-stone-900 mb-2"
                    >
                      Special Requests or Questions
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                      placeholder="Let us know about any special occasions, dietary requirements, or questions you may have..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-stone-900 text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors disabled:bg-stone-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send size={18} />
                        Send Inquiry
                      </>
                    )}
                  </button>

                  <p className="text-xs text-stone-500 text-center">
                    By submitting this form, you agree to our privacy policy. We will never share
                    your information with third parties.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[500px] bg-stone-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d251446.0294851482!2d99.82545825!3d9.5203761!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3054f14a809b68e5%3A0x40223bc2c382480!2sKo%20Samui%2C%20Surat%20Thani%2C%20Thailand!5e0!3m2!1sen!2sus!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="The Amor Location"
        />
      </section>
    </Layout>
  );
}
